// File: src/pages/VideoGenerationTestPage.tsx

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box, Button, Card, CardContent, FormControl, InputLabel, MenuItem, Select, Typography, CircularProgress,
    Alert, TextField, Tooltip, Grid, IconButton, Accordion, AccordionSummary, AccordionDetails, Paper
} from '@mui/material';
import { Search as SearchIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import { Product, AIService, PromptTemplate, VideoGeneration } from '../types';
import { getProducts, fetchAIServices, getPromptTemplates, getVideoGenerationsForProduct, getSingleVideoGeneration } from '../services/api';
import { useVideoGeneration } from '../hooks/useVideoGeneration';

import PromptViewerDialog from '../components/PromptViewerDialog';
import AIGuideDialog from '../components/AIGuideDialog';
import VideoGenerationHistoryDialog from '../components/video_gen/VideoGenerationHistoryDialog';

const VideoGenerationTestPage: React.FC = () => {
    // --- Состояния UI (без изменений) ---
    const [selectedProductId, setSelectedProductId] = useState<number | ''>('');
    const [selectedAIServiceCode, setSelectedAIServiceCode] = useState<string>('');
    const [selectedVideoServiceCode, setSelectedVideoServiceCode] = useState<string>('');
    const [selectedPromptTemplateId, setSelectedPromptTemplateId] = useState<number | ''>('');
    const [additionalInstructions, setAdditionalInstructions] = useState<string>('');
    const [selectedReferenceImageUrl, setSelectedReferenceImageUrl] = useState<string | null>(null);

    const [historyOpen, setHistoryOpen] = useState(false);
    const [isAIGuideOpen, setIsAIGuideOpen] = useState(false);
    const [viewedAIService, setViewedAIService] = useState<AIService | null>(null);
    const [isPromptViewerOpen, setIsPromptViewerOpen] = useState(false);
    const [viewedPromptTemplate, setViewedPromptTemplate] = useState<PromptTemplate | null>(null);
    const [promptExpanded, setPromptExpanded] = useState<boolean>(true);

    // --- Запросы к API (без изменений) ---
    const { data: products } = useQuery<Product[], Error>(['products'], getProducts);
    const { data: allAiServices } = useQuery<AIService[], Error>(['aiServices'], () => fetchAIServices());
    const { data: promptTemplates } = useQuery<PromptTemplate[], Error>(['promptTemplatesVideo'], () => getPromptTemplates('video_generation'));
    const { data: historyGenerations = [], isLoading: isLoadingHistory } = useQuery<VideoGeneration[], Error>(
        ['videoGenerationHistory', selectedProductId],
        () => getVideoGenerationsForProduct(selectedProductId as number),
        { enabled: !!selectedProductId }
    );

    // ✅ ИСПРАВЛЕНИЕ: Сначала объявляем хук, который предоставляет generationId
    const {
        generationId,
        generationStep,
        error,
        promptGenMutation,
        videoGenMutation,
        resetGeneration,
        handleLoadGeneration,
        canStartPromptGen,
        canStartVideoGen,
        selectedPromptTemplate,
        selectedAIService,
        selectedVideoService,
    } = useVideoGeneration({
        selectedProductId,
        selectedAIServiceCode,
        selectedVideoServiceCode,
        selectedPromptTemplateId,
        additionalInstructions,
        selectedReferenceImageUrl,
        allAiServices,
        promptTemplates,
    });

    // ✅ ИСПРАВЛЕНИЕ: Теперь используем generationId в useQuery, так как он уже определен.
    const { data: currentGeneration } = useQuery<VideoGeneration, Error>({
        queryKey: ['videoGenerationStatus', generationId],
        queryFn: () => getSingleVideoGeneration(selectedProductId as number, generationId as number),
        enabled: !!generationId,
        refetchInterval: (data) => {
            if (data && ['pending', 'prompt_generating', 'video_generating'].includes(data.status)) {
                return 3000;
            }
            return false;
        },
    });

    // ✅ ИСПРАВЛЕНИЕ: Передаем обновленный currentGeneration в хук через useEffect.
    useEffect(() => {
        if (currentGeneration) {
            handleLoadGeneration(currentGeneration);
        }
    }, [currentGeneration, handleLoadGeneration]);

    // --- Остальной код ---
    const textAiServices = useMemo(() => allAiServices?.filter(s => s.is_active && s.supported_tasks.includes('prompt_generation')) || [], [allAiServices]);
    const videoAiServices = useMemo(() => allAiServices?.filter(s => s.is_active && s.supported_tasks.includes('video_generation')) || [], [allAiServices]);
    const selectedProduct = useMemo(() => products?.find(p => p.id === selectedProductId), [products, selectedProductId]);
    useEffect(() => { if (promptTemplates?.length === 1 && !selectedPromptTemplateId) setSelectedPromptTemplateId(promptTemplates[0].id) }, [promptTemplates, selectedPromptTemplateId]);
    useEffect(() => { if (videoAiServices?.length === 1 && !selectedVideoServiceCode) setSelectedVideoServiceCode(videoAiServices[0].code) }, [videoAiServices, selectedVideoServiceCode]);
    useEffect(() => { if (textAiServices?.length === 1 && !selectedAIServiceCode) setSelectedAIServiceCode(textAiServices[0].code) }, [textAiServices, selectedAIServiceCode]);
    const resetAll = () => { setSelectedProductId(''); setSelectedAIServiceCode(''); setSelectedVideoServiceCode(''); setSelectedPromptTemplateId(''); setAdditionalInstructions(''); setSelectedReferenceImageUrl(null); resetGeneration(); };
    const handleHistoryLoad = (gen: VideoGeneration) => { setHistoryOpen(false); handleLoadGeneration(gen); setSelectedProductId(gen.product_id); setSelectedReferenceImageUrl(gen.reference_image_url || null); setSelectedAIServiceCode(gen.ai_service_used); setSelectedVideoServiceCode(gen.video_ai_service_used); setSelectedPromptTemplateId(gen.prompt_template_id || ''); setAdditionalInstructions(gen.extra_instructions || ''); };
    const handleViewAIService = (service: AIService | null) => { setViewedAIService(service); setIsAIGuideOpen(true); };
    const handleViewPromptTemplate = (template: PromptTemplate | null) => { setViewedPromptTemplate(template); setIsPromptViewerOpen(true); };

    // ✅ ИСПРАВЛЕНИЕ 1: Добавляем новые статусы ошибок
    const statusMessages: Record<string, string> = {
        prompt_generating: "Generating prompt...",
        prompt_ready: "Prompt is ready, you can start video generation.",
        video_generating: "Generating video, this may take a moment...",
        prompt_failed: "Prompt generation failed. Please check the settings and try again.",
        video_failed: "Video generation failed. You can try to generate it again or reset.",
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom>Image-to-Video Generation (Test)</Typography>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>1. Product & Reference Image</Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Select product</InputLabel>
                                <Select value={selectedProductId} label="Select product" onChange={e => { setSelectedProductId(e.target.value as number); resetGeneration(); }}>
                                    {products?.map(p => <MenuItem key={p.id} value={p.id}>{p.name} {p.brand ? `(${p.brand})` : ''}</MenuItem>)}
                                </Select>
                            </FormControl>
                            {selectedProduct && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle1" gutterBottom>Select Reference Image:</Typography>
                                    {(selectedProduct.images?.length > 0) ? (
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: 150, overflowY: 'auto', p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                            {selectedProduct.images.map(url => <Box key={url} sx={{ width: 80, height: 80, cursor: 'pointer', borderRadius: 1, overflow: 'hidden', border: `2px solid ${url === selectedReferenceImageUrl ? '#1976d2' : 'transparent'}`, '&:hover': { borderColor: url === selectedReferenceImageUrl ? '#1976d2' : '#90caf9' }, transition: 'border-color 0.2s', }} onClick={() => setSelectedReferenceImageUrl(url)}><img src={url} alt="Product reference" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Box>)}
                                        </Box>
                                    ) : ( <Typography color="text.secondary" variant="body2">This product has no images.</Typography> )}
                                </Box>
                            )}
                            {selectedProductId && <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setHistoryOpen(true)} size="small" disabled={isLoadingHistory}>View History ({historyGenerations.length})</Button>}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>2. AI Settings</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small"><InputLabel>AI Service for Prompt</InputLabel><Select value={selectedAIServiceCode} label="AI Service for Prompt" onChange={e => setSelectedAIServiceCode(e.target.value)} disabled={generationStep !== 'idle'}>{textAiServices?.map(s => <MenuItem key={s.id} value={s.code}>{s.name}</MenuItem>)}</Select></FormControl>
                                {selectedAIService && <Tooltip title="View AI Service Details"><IconButton onClick={() => handleViewAIService(selectedAIService)} disabled={generationStep !== 'idle'}><SearchIcon color="action" /></IconButton></Tooltip>}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small"><InputLabel>AI Service for Video</InputLabel><Select value={selectedVideoServiceCode} label="AI Service for Video" onChange={e => setSelectedVideoServiceCode(e.target.value)} disabled={generationStep !== 'idle'}>{videoAiServices?.map(s => <MenuItem key={s.id} value={s.code}>{s.name}</MenuItem>)}</Select></FormControl>
                                {selectedVideoService && <Tooltip title="View AI Service Details"><IconButton onClick={() => handleViewAIService(selectedVideoService)} disabled={generationStep !== 'idle'}><SearchIcon color="action" /></IconButton></Tooltip>}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small"><InputLabel>Prompt Template</InputLabel><Select value={selectedPromptTemplateId} label="Prompt Template" onChange={e => setSelectedPromptTemplateId(e.target.value as number)} disabled={generationStep !== 'idle'}><MenuItem value="">None</MenuItem>{promptTemplates?.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}</Select></FormControl>
                                {selectedPromptTemplate && <Tooltip title="View Prompt Template"><IconButton onClick={() => handleViewPromptTemplate(selectedPromptTemplate)} disabled={generationStep !== 'idle'}><SearchIcon color="action" /></IconButton></Tooltip>}
                            </Box>
                            <TextField fullWidth multiline minRows={1} maxRows={3} label="Additional Instructions (optional)" value={additionalInstructions} onChange={e => setAdditionalInstructions(e.target.value)} disabled={true} placeholder="e.g., dynamic camera movement, cinematic style"/>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                                <Button variant="contained" onClick={() => promptGenMutation.mutate()} disabled={!canStartPromptGen || promptGenMutation.isLoading}>{promptGenMutation.isLoading || generationStep === 'prompt_generating' ? <CircularProgress size={24} /> : 'Generate Prompt'}</Button>
                                <Button variant="outlined" onClick={() => videoGenMutation.mutate()} disabled={!canStartVideoGen || videoGenMutation.isLoading}>{videoGenMutation.isLoading || generationStep === 'video_generating' ? <CircularProgress size={24} /> : 'Generate Video'}</Button>
                                <Button variant="text" color="secondary" onClick={resetAll} disabled={promptGenMutation.isLoading || videoGenMutation.isLoading}>Reset</Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {generationStep !== 'idle' && (
                <Accordion expanded={promptExpanded} onChange={() => setPromptExpanded(!promptExpanded)} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Generated Prompt</Typography></AccordionSummary>
                    <AccordionDetails>
                        {currentGeneration?.prompt_generation_result_parsed?.prompt ? (
                            <Box sx={{ p: 1, border: '1px dashed', borderRadius: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap', bgcolor: 'background.paper' }}>
                                {currentGeneration.prompt_generation_result_parsed.prompt}
                            </Box>
                        ) : ( <Typography color="text.secondary">Prompt is being generated...</Typography> )}
                    </AccordionDetails>
                </Accordion>
            )}

            {generationStep !== 'idle' && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Result</Typography>
                        {/* ✅ ИСПРАВЛЕНИЕ 2: Обновляем логику отображения статусов и ошибок */}
                        {['prompt_generating', 'video_generating'].includes(generationStep) && (
                            <Box sx={{ textAlign: 'center', my: 4 }}><CircularProgress /><Typography sx={{ mt: 2 }}>{statusMessages[generationStep] || 'Processing...'}</Typography></Box>
                        )}
                        {generationStep === 'prompt_ready' && (
                            <Alert severity="success">{statusMessages[generationStep]}</Alert>
                        )}
                        {generationStep === 'prompt_failed' && (
                            <Alert severity="error">{statusMessages[generationStep]}</Alert>
                        )}
                        {generationStep === 'video_failed' && (
                            <Alert severity="error">{statusMessages[generationStep]}</Alert>
                        )}
                        {currentGeneration?.status === 'completed' && currentGeneration?.video_result_details?.video_url && (
                            <Box sx={{ mt: 2 }}><video src={currentGeneration.video_result_details.video_url} width="100%" controls autoPlay loop style={{ borderRadius: '8px' }} /><Typography variant="caption" component="div" sx={{ mt: 1 }}>Duration: {currentGeneration.video_result_details.duration_seconds?.toFixed(1)}s</Typography><Typography variant="caption" component="div">Cost: ${currentGeneration.total_cost?.toFixed(4)}</Typography></Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {currentGeneration && (currentGeneration.ai_raw_prompt || currentGeneration.ai_raw_response) && (
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Debug Info (Raw AI Data)</Typography></AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="overline">Raw Prompt sent to LLM</Typography>
                        <Paper variant="outlined" sx={{ p: 1, mb: 2, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {currentGeneration.ai_raw_prompt || 'Not available.'}
                        </Paper>
                        <Typography variant="overline">Raw Response from LLM</Typography>
                        <Paper variant="outlined" sx={{ p: 1, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {currentGeneration.ai_raw_response || 'Not available.'}
                        </Paper>
                    </AccordionDetails>
                </Accordion>
            )}
            <VideoGenerationHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} historyGenerations={historyGenerations} product={selectedProduct} onLoadGeneration={handleHistoryLoad} isLoading={isLoadingHistory} />
            <AIGuideDialog open={isAIGuideOpen} onClose={() => setIsAIGuideOpen(false)} aiService={viewedAIService} />
            <PromptViewerDialog open={isPromptViewerOpen} onClose={() => setIsPromptViewerOpen(false)} prompt={viewedPromptTemplate} />
        </Box>
    );
};

export default VideoGenerationTestPage;